import { ApiError } from "api/client";
import { addEntityTreeLink, createEntity, getTreeEntities, removeEntityTreeLink, updateEntity } from "api/entities";
import { addRelation, getEntityRelations, removeRelation } from "api/relations";
import type { CustomPropertyDto, EntityDto, EntityRelationDto, LinkedTreeDto, ReferenceLinkDto } from "api/types";
import { fieldLimits } from "config";
import { useDialogFocus } from "hooks/useDialogFocus";
import React, { type ChangeEvent, type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";
import { flipRelationPerspective } from "utilities";

import CustomAttrRow from "./CustomAttrRow";
import { byEntityTitle, UNRESTRICTED_OPTIONS } from "./entityFormConfigs";
import type {
	CustomProp,
	EntityDialogProps,
	ObjectFormState,
	ReferenceLinkDraft,
	RelationDraft,
	RelationOption,
	TreeLinkDraft
} from "./entityForms.types";
import EntityImageGallery from "./EntityImageGallery";
import LineIcon from "./LineIcon";
import ReferenceLinkRow from "./ReferenceLinkRow";
import RelationChipItem from "./RelationChipItem";
import RelationTypePicker from "./RelationTypePicker";
import TreeLinkPicker from "./TreeLinkPicker";
import { useEntityImages } from "./useEntityImages";

import styles from "./formDialog.module.css";

// Every option reads "this object is <type> of/at the target", so the forward edge is always From = this object.
const ASSOCIATION_OPTIONS: RelationOption[] = [
	{ value: "BelongsTo", targetTypes: ["Person"], canBeEx: true },
	{ value: "StoredAt", targetTypes: ["Place"], canBeEx: true },
	{ value: "ForgedBy", targetTypes: ["Person", "Organization"] },
	{ value: "GiftedTo", targetTypes: ["Person"] },
	{ value: "InheritedBy", targetTypes: ["Person", "Organization"] },
	{ value: "OriginatesFrom", targetTypes: ["Place", "Organization"] },
	{ value: "PrimaryWeapon", targetTypes: ["Person"], canBeEx: true },
	// The API takes an Object on the From side of UsedBy, but only the Custom form offered it — so an
	// object could never be marked as used by anyone.
	{ value: "UsedBy", targetTypes: ["Person", "Organization", "Species"], canBeEx: true },
	...UNRESTRICTED_OPTIONS,
	{ value: "Other", targetTypes: ["Person", "Place", "Object", "Creature", "Organization"], canBeEx: true }
];

const OBJECT_TYPE_SUGGESTIONS = [
	"Weapon",
	"Armor",
	"Artifact",
	"Heirloom",
	"Relic",
	"Jewelry",
	"Book",
	"Tool",
	"Instrument",
	"Clothing",
	"Vehicle"
];

const newId = (): string =>
	typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

const createInitialForm = (entity?: EntityDto | null): ObjectFormState => ({
	currentLocation: entity?.currentLocation ?? "",
	description: entity?.description ?? "",
	era: entity?.era ?? "",
	material: entity?.material ?? "",
	name: entity?.title ?? "",
	objectType: entity?.objectType ?? "",
	origin: entity?.origin ?? ""
});

const ObjectFormDialog = ({ entity, treeId, onClose, onCreated }: EntityDialogProps) => {
	const { t } = useTranslation("homePage");
	const { getAccessToken } = useSession();
	const dialogRef = useDialogFocus<HTMLFormElement>();
	const isEdit = Boolean(entity);

	const [form, setForm] = useState<ObjectFormState>(() => createInitialForm(entity));
	const gallery = useEntityImages({
		entityId: entity?.id ?? null,
		initialImages: entity?.images,
		getAccessToken
	});
	const [customProps, setCustomProps] = useState<CustomProp[]>(
		() =>
			entity?.customProperties?.map((prop: CustomPropertyDto) => ({
				id: prop.id,
				key: prop.key,
				value: prop.value ?? ""
			})) ?? []
	);
	const [referenceLinks, setReferenceLinks] = useState<ReferenceLinkDraft[]>(
		() => entity?.referenceLinks?.map((link: ReferenceLinkDto) => ({ id: link.id, url: link.url })) ?? []
	);
	const [treeLinks, setTreeLinks] = useState<TreeLinkDraft[]>(
		() =>
			entity?.linkedTrees?.map((link: LinkedTreeDto) => ({
				id: link.id,
				existingId: link.id,
				treeId: link.treeId,
				title: link.title
			})) ?? []
	);
	const [removedTreeLinkIds, setRemovedTreeLinkIds] = useState<string[]>([]);
	const [treeEntities, setTreeEntities] = useState<EntityDto[]>([]);
	const [associationType, setAssociationType] = useState<string>("");
	const [associationTarget, setAssociationTarget] = useState<string>("");
	const [associationIsEx, setAssociationIsEx] = useState(false);
	const [associations, setAssociations] = useState<RelationDraft[]>([]);
	const [removedAssociationIds, setRemovedAssociationIds] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState("");

	// Null until a type is picked, so an untouched dropdown can no longer record a relation
	// the author never chose. Everything downstream must cope with "nothing selected yet".
	const selectedOption = useMemo<RelationOption | null>(
		() => ASSOCIATION_OPTIONS.find((option: RelationOption) => option.value === associationType) ?? null,
		[associationType]
	);
	const availableTargets = useMemo<EntityDto[]>(
		() =>
			selectedOption
				? treeEntities
						.filter((item: EntityDto) => selectedOption.targetTypes.includes(item.type))
						.sort(byEntityTitle)
				: [],
		[selectedOption, treeEntities]
	);
	useEffect(() => {
		let active = true;

		getAccessToken()
			.then((token: string) => (token ? getTreeEntities(treeId, token) : null))
			.then((list: EntityDto[] | null) => {
				if (active && list) {
					setTreeEntities(list.filter((item: EntityDto) => item.id !== entity?.id));
				}
			})
			.catch(() => undefined);

		return () => {
			active = false;
		};
	}, [entity?.id, getAccessToken, treeId]);

	// Edit mode: show the object's existing associations so they can be removed.
	useEffect(() => {
		if (!entity) {
			return undefined;
		}

		let active = true;

		getAccessToken()
			.then((token: string) => (token ? getEntityRelations(entity.id, token) : null))
			.then((list: EntityRelationDto[] | null) => {
				if (active && list) {
					setAssociations(
						list.map((relation: EntityRelationDto) => ({
							id: relation.id,
							existingId: relation.id,
							relationType: flipRelationPerspective(relation.relationType),
							isEx: relation.isEx,
							targetId: relation.relatedEntityId,
							targetTitle: relation.relatedEntityTitle
						}))
					);
				}
			})
			.catch(() => undefined);

		return () => {
			active = false;
		};
	}, [entity, getAccessToken]);

	const updateField = useCallback(
		(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
			const { name, value } = event.target;
			setForm((current: ObjectFormState) => ({ ...current, [name]: value }));
		},
		[]
	);

	const addCustomProp = useCallback((): void => {
		setCustomProps((current: CustomProp[]) => [...current, { id: newId(), key: "", value: "" }]);
	}, []);

	const updateCustomProp = useCallback((id: string, field: "key" | "value", value: string): void => {
		setCustomProps((current: CustomProp[]) =>
			current.map((prop: CustomProp) => (prop.id === id ? { ...prop, [field]: value } : prop))
		);
	}, []);

	const removeCustomProp = useCallback((id: string): void => {
		setCustomProps((current: CustomProp[]) => current.filter((prop: CustomProp) => prop.id !== id));
	}, []);

	const addReferenceLink = useCallback((): void => {
		setReferenceLinks((current: ReferenceLinkDraft[]) => [...current, { id: newId(), url: "" }]);
	}, []);

	const updateReferenceLink = useCallback((id: string, url: string): void => {
		setReferenceLinks((current: ReferenceLinkDraft[]) =>
			current.map((link: ReferenceLinkDraft) => (link.id === id ? { ...link, url } : link))
		);
	}, []);

	const removeReferenceLink = useCallback((id: string): void => {
		setReferenceLinks((current: ReferenceLinkDraft[]) =>
			current.filter((link: ReferenceLinkDraft) => link.id !== id)
		);
	}, []);

	const addTreeLink = useCallback((linkTreeId: string, title: string): void => {
		setTreeLinks((current: TreeLinkDraft[]) => [...current, { id: newId(), treeId: linkTreeId, title }]);
	}, []);

	const removeTreeLink = useCallback(
		(id: string): void => {
			const removed = treeLinks.find((link: TreeLinkDraft) => link.id === id);
			if (removed?.existingId) {
				const existingId = removed.existingId;
				setRemovedTreeLinkIds((ids: string[]) => [...ids, existingId]);
			}

			setTreeLinks((current: TreeLinkDraft[]) => current.filter((link: TreeLinkDraft) => link.id !== id));
		},
		[treeLinks]
	);

	const addRelationDraft = useCallback((): void => {
		if (!selectedOption || !associationTarget) {
			return;
		}

		const target = availableTargets.find((item: EntityDto) => item.id === associationTarget);
		if (!target) {
			return;
		}

		setAssociations((current: RelationDraft[]) => [
			...current,
			{
				id: newId(),
				relationType: associationType,
				isEx: Boolean(selectedOption.canBeEx) && associationIsEx,
				targetId: target.id,
				targetTitle: target.title
			}
		]);
		setAssociationTarget("");
		setAssociationIsEx(false);
	}, [associationIsEx, associationTarget, associationType, availableTargets, selectedOption]);

	const removeRelationDraft = useCallback(
		(id: string): void => {
			const removed = associations.find((association: RelationDraft) => association.id === id);
			if (removed?.existingId) {
				const existingId = removed.existingId;
				setRemovedAssociationIds((ids: string[]) => [...ids, existingId]);
			}

			setAssociations((current: RelationDraft[]) =>
				current.filter((association: RelationDraft) => association.id !== id)
			);
		},
		[associations]
	);

	const onAssociationTypeChange = useCallback((nextType: string): void => {
		setAssociationType(nextType);
		// The new type may allow a different set of targets, and may not support the ex modifier.
		setAssociationTarget("");
		setAssociationIsEx(false);
	}, []);

	const onAssociationTargetChange = useCallback((event: ChangeEvent<HTMLSelectElement>): void => {
		setAssociationTarget(event.target.value);
	}, []);

	const onAssociationIsExChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
		setAssociationIsEx(event.target.checked);
	}, []);

	const onSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();

			const name = form.name.trim();
			if (!name) {
				setError(t("editor.objectForm.nameRequired"));

				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			setError("");
			setIsSaving(true);

			try {
				const fields = {
					title: name,
					objectType: form.objectType.trim() || undefined,
					origin: form.origin.trim() || undefined,
					material: form.material.trim() || undefined,
					currentLocation: form.currentLocation.trim() || undefined,
					era: form.era.trim() || undefined,
					// Ownership is managed via the BelongsTo association; preserve any FK set elsewhere.
					ownerEntityId: entity?.ownerEntityId ?? undefined,
					description: form.description.trim() || undefined,
					customProperties: customProps
						.filter((prop: CustomProp) => prop.key.trim())
						.map((prop: CustomProp) => ({
							key: prop.key.trim(),
							value: prop.value.trim() || undefined
						})),
					referenceLinks: referenceLinks.map((link: ReferenceLinkDraft) => link.url.trim()).filter(Boolean)
				};

				const saved = entity
					? await updateEntity(entity.id, { ...fields, images: gallery.toInputPayload() }, token)
					: await createEntity(
							treeId,
							{ type: "Object", ...fields, images: gallery.toInputPayload() },
							token
					  );

				await Promise.all(removedAssociationIds.map((relationId: string) => removeRelation(relationId, token)));

				// Every association reads "this object is <type> of/at the target", so this object is the From side.
				await Promise.all(
					associations
						.filter((association: RelationDraft) => !association.existingId)
						.map((association: RelationDraft) =>
							addRelation(
								{
									fromEntityId: saved.id,
									toEntityId: association.targetId,
									relationType: association.relationType,
									isEx: association.isEx
								},
								token
							)
						)
				);

				await Promise.all(removedTreeLinkIds.map((linkId: string) => removeEntityTreeLink(linkId, token)));
				await Promise.all(
					treeLinks
						.filter((link: TreeLinkDraft) => !link.existingId)
						.map((link: TreeLinkDraft) => addEntityTreeLink(saved.id, link.treeId, token))
				);

				onCreated(saved.id);
				onClose();
			} catch (submitError) {
				setError(submitError instanceof ApiError ? submitError.message : t("editor.objectForm.saveError"));
			} finally {
				setIsSaving(false);
			}
		},
		[
			associations,
			customProps,
			entity,
			form,
			gallery,
			getAccessToken,
			onClose,
			onCreated,
			referenceLinks,
			removedAssociationIds,
			removedTreeLinkIds,
			t,
			treeId,
			treeLinks
		]
	);

	const hasTargets = availableTargets.length > 0;

	return (
		<div className={styles.personPanelOverlay}>
			<form
				ref={dialogRef}
				aria-labelledby="object-form-title"
				aria-modal="true"
				className={styles.personFormPanel}
				onSubmit={onSubmit}
				role="dialog">
				<div className={styles.personFormHeader}>
					<div className={styles.personFormHeaderCopy}>
						<h1 id="object-form-title">
							{isEdit ? t("editor.objectForm.editTitle") : t("editor.objectForm.addTitle")}
						</h1>
						<p>{t("editor.objectForm.subtitle")}</p>
					</div>

					<button
						aria-label={t("editor.objectForm.close")}
						className={styles.personFormCloseButton}
						onClick={onClose}
						type="button">
						<LineIcon name="close" />
					</button>
				</div>

				<div className={styles.personFormBody}>
					<div className={styles.personFormProfile}>
						<EntityImageGallery gallery={gallery} title={form.name} editable />
					</div>

					<div className={styles.personFormFields}>
						<h2>{t("editor.objectForm.standardAttributes")}</h2>

						<div className={styles.personFormGrid}>
							<div className={styles.personFormField}>
								<label htmlFor="object-name">
									{t("editor.objectForm.title")} <span className={styles.requiredStar}>*</span>
								</label>
								<input
									id="object-name"
									maxLength={fieldLimits.entityTitle}
									name="name"
									onChange={updateField}
									type="text"
									value={form.name}
									required
								/>
							</div>

							<div className={styles.personFormField}>
								<label htmlFor="object-type">{t("editor.objectForm.type")}</label>
								<input
									id="object-type"
									list="object-type-suggestions"
									maxLength={fieldLimits.customPropertyValue}
									name="objectType"
									onChange={updateField}
									type="text"
									value={form.objectType}
								/>
								<datalist id="object-type-suggestions">
									{OBJECT_TYPE_SUGGESTIONS.map((suggestion: string) => (
										<option key={suggestion} label={suggestion} value={suggestion} />
									))}
								</datalist>
							</div>

							<div className={styles.personFormField}>
								<label htmlFor="object-origin">{t("editor.objectForm.origin")}</label>
								<input
									id="object-origin"
									maxLength={fieldLimits.customPropertyValue}
									name="origin"
									onChange={updateField}
									type="text"
									value={form.origin}
								/>
							</div>

							<div className={styles.personFormField}>
								<label htmlFor="object-material">{t("editor.objectForm.material")}</label>
								<input
									id="object-material"
									maxLength={fieldLimits.customPropertyValue}
									name="material"
									onChange={updateField}
									type="text"
									value={form.material}
								/>
							</div>

							<div className={styles.personFormField}>
								<label htmlFor="object-location">{t("editor.objectForm.currentLocation")}</label>
								<input
									id="object-location"
									maxLength={fieldLimits.customPropertyValue}
									name="currentLocation"
									onChange={updateField}
									type="text"
									value={form.currentLocation}
								/>
							</div>

							<div className={`${styles.personFormField} ${styles.personFormFieldFull}`}>
								<label htmlFor="object-era">{t("editor.objectForm.era")}</label>
								<input
									id="object-era"
									maxLength={fieldLimits.customPropertyValue}
									name="era"
									onChange={updateField}
									type="text"
									value={form.era}
								/>
							</div>

							<div className={`${styles.personFormField} ${styles.personFormFieldFull}`}>
								<label htmlFor="object-description">{t("editor.objectForm.description")}</label>
								<textarea
									id="object-description"
									maxLength={fieldLimits.customPropertyValue}
									name="description"
									onChange={updateField}
									rows={4}
									value={form.description}
								/>
							</div>
						</div>
					</div>

					<section className={styles.personFormSection}>
						<h2>{t("editor.objectForm.associations")}</h2>
						<div className={styles.relationAddRow}>
							<RelationTypePicker
								controlClassName={styles.relationSelect}
								disabled={isSaving}
								onChange={onAssociationTypeChange}
								options={ASSOCIATION_OPTIONS}
								value={associationType}
							/>
							<select
								aria-label={t("editor.objectForm.associationTarget")}
								className={styles.relationSelect}
								disabled={!hasTargets || isSaving}
								onChange={onAssociationTargetChange}
								value={associationTarget}>
								<option value="">{t("editor.objectForm.associationTargetPlaceholder")}</option>
								{availableTargets.map((target: EntityDto) => (
									<option key={target.id} value={target.id}>
										{target.type === "Person" ? target.title : `${target.title} (${target.type})`}
									</option>
								))}
							</select>

							{selectedOption?.canBeEx && (
								<label className={styles.relationExToggle} htmlFor="association-is-ex">
									<input
										checked={associationIsEx}
										disabled={isSaving}
										id="association-is-ex"
										onChange={onAssociationIsExChange}
										type="checkbox"
									/>
									<span>{t("editor.objectForm.formerAssociation")}</span>
								</label>
							)}
							<button
								className={styles.relationAddButton}
								disabled={!associationType || !associationTarget || isSaving}
								onClick={addRelationDraft}
								type="button">
								<LineIcon name="plus" />
								<span>{t("editor.objectForm.addAssociation")}</span>
							</button>
						</div>

						{!selectedOption && (
							<p className={styles.relationHint}>{t("editor.relationPicker.chooseTypeHint")}</p>
						)}
						{selectedOption && !hasTargets && (
							<p className={styles.relationHint}>{t("editor.objectForm.noTargetsHint")}</p>
						)}

						{associations.length > 0 && (
							<div className={styles.relationChips}>
								{associations.map((association: RelationDraft) => (
									<RelationChipItem
										key={association.id}
										onRemove={removeRelationDraft}
										relation={association}
										removeLabel={t("editor.objectForm.removeAssociation")}
									/>
								))}
							</div>
						)}
					</section>

					<TreeLinkPicker
						currentTreeId={treeId}
						links={treeLinks}
						onAdd={addTreeLink}
						onRemove={removeTreeLink}
					/>

					<section className={styles.personFormSection}>
						<h2>{t("editor.objectForm.customAttributes")}</h2>
						{customProps.map((prop: CustomProp) => (
							<CustomAttrRow
								key={prop.id}
								idPrefix="object"
								keyLabel={t("editor.objectForm.key")}
								onChange={updateCustomProp}
								onRemove={removeCustomProp}
								prop={prop}
								removeLabel={t("editor.objectForm.removeCustomAttribute")}
								valueLabel={t("editor.objectForm.value")}
							/>
						))}
						<button className={styles.customAttrAddButton} onClick={addCustomProp} type="button">
							<LineIcon name="plus" />
							<span>{t("editor.objectForm.addCustomAttribute")}</span>
						</button>
					</section>

					<section className={styles.personFormSection}>
						<h2>{t("editor.objectForm.references")}</h2>
						{referenceLinks.map((link: ReferenceLinkDraft) => (
							<ReferenceLinkRow
								key={link.id}
								link={link}
								onChange={updateReferenceLink}
								onRemove={removeReferenceLink}
								removeLabel={t("editor.objectForm.removeReference")}
								urlLabel={t("editor.objectForm.referenceUrl")}
							/>
						))}
						<button className={styles.customAttrAddButton} onClick={addReferenceLink} type="button">
							<LineIcon name="plus" />
							<span>{t("editor.objectForm.addReference")}</span>
						</button>
					</section>

					{error && <div className={styles.personFormError}>{error}</div>}
				</div>

				<div className={styles.personFormActions}>
					<button
						className={styles.personFormCancelButton}
						disabled={isSaving}
						onClick={onClose}
						type="button">
						{t("editor.objectForm.cancel")}
					</button>
					<button className={styles.personFormSaveButton} disabled={isSaving} type="submit">
						{isSaving ? t("editor.objectForm.saving") : t("editor.objectForm.save")}
					</button>
				</div>
			</form>
		</div>
	);
};

export default ObjectFormDialog;
